import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { createMission } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Loader2, MapPin, Package, Calendar, Euro } from 'lucide-react';
import { toast } from 'sonner';

const NewMissionPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    pickup_address: '',
    pickup_lat: 48.8566,
    pickup_lng: 2.3522,
    delivery_address: '',
    delivery_lat: 45.7640,
    delivery_lng: 4.8357,
    cargo_type: '',
    weight_kg: '',
    volume_m3: '',
    required_vehicle_type: 'truck',
    deadline: '',
    budget_min: '',
    budget_max: '',
    special_requirements: []
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSelectChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const missionData = {
        ...formData,
        weight_kg: parseFloat(formData.weight_kg),
        volume_m3: parseFloat(formData.volume_m3),
        budget_min: parseFloat(formData.budget_min),
        budget_max: parseFloat(formData.budget_max),
        pickup_lat: parseFloat(formData.pickup_lat),
        pickup_lng: parseFloat(formData.pickup_lng),
        delivery_lat: parseFloat(formData.delivery_lat),
        delivery_lng: parseFloat(formData.delivery_lng)
      };

      await createMission(missionData);
      toast.success('Mission créée avec succès !');
      navigate('/missions');
    } catch (error) {
      console.error('Failed to create mission:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-3xl mx-auto" data-testid="new-mission-page">
        <Button 
          variant="ghost" 
          onClick={() => navigate(-1)} 
          className="mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour
        </Button>

        <Card>
          <CardHeader>
            <CardTitle className="text-2xl font-['Chivo']">Nouvelle Mission</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Basic Info */}
              <div className="space-y-4">
                <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                  <Package className="w-5 h-5 text-orange-500" />
                  Informations générales
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="md:col-span-2 space-y-2">
                    <Label htmlFor="title">Titre de la mission *</Label>
                    <Input
                      id="title"
                      name="title"
                      value={formData.title}
                      onChange={handleChange}
                      placeholder="Ex: Transport matériel électronique"
                      required
                      data-testid="mission-title-input"
                    />
                  </div>
                  <div className="md:col-span-2 space-y-2">
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      placeholder="Décrivez les détails de la mission..."
                      rows={3}
                      data-testid="mission-description-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="cargo_type">Type de marchandise *</Label>
                    <Input
                      id="cargo_type"
                      name="cargo_type"
                      value={formData.cargo_type}
                      onChange={handleChange}
                      placeholder="Ex: Électronique, Alimentaire..."
                      required
                      data-testid="mission-cargo-type-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="required_vehicle_type">Type de véhicule requis *</Label>
                    <Select 
                      value={formData.required_vehicle_type} 
                      onValueChange={(v) => handleSelectChange('required_vehicle_type', v)}
                    >
                      <SelectTrigger data-testid="mission-vehicle-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="truck">Camion</SelectItem>
                        <SelectItem value="van">Fourgon</SelectItem>
                        <SelectItem value="refrigerated">Frigorifique</SelectItem>
                        <SelectItem value="flatbed">Plateau</SelectItem>
                        <SelectItem value="tanker">Citerne</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Locations */}
              <div className="space-y-4">
                <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-orange-500" />
                  Adresses
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="pickup_address">Adresse d&apos;enlèvement *</Label>
                    <Input
                      id="pickup_address"
                      name="pickup_address"
                      value={formData.pickup_address}
                      onChange={handleChange}
                      placeholder="Ex: 12 rue de la Paix, Paris"
                      required
                      data-testid="mission-pickup-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="delivery_address">Adresse de livraison *</Label>
                    <Input
                      id="delivery_address"
                      name="delivery_address"
                      value={formData.delivery_address}
                      onChange={handleChange}
                      placeholder="Ex: 45 avenue Jean Jaurès, Lyon"
                      required
                      data-testid="mission-delivery-input"
                    />
                  </div>
                </div>
              </div>

              {/* Cargo Details */}
              <div className="space-y-4">
                <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                  <Package className="w-5 h-5 text-orange-500" />
                  Détails du chargement
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="weight_kg">Poids (kg) *</Label>
                    <Input
                      id="weight_kg"
                      name="weight_kg"
                      type="number"
                      value={formData.weight_kg}
                      onChange={handleChange}
                      placeholder="500"
                      required
                      min="0"
                      data-testid="mission-weight-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="volume_m3">Volume (m³) *</Label>
                    <Input
                      id="volume_m3"
                      name="volume_m3"
                      type="number"
                      value={formData.volume_m3}
                      onChange={handleChange}
                      placeholder="10"
                      required
                      min="0"
                      step="0.1"
                      data-testid="mission-volume-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="budget_min">Budget min (€) *</Label>
                    <Input
                      id="budget_min"
                      name="budget_min"
                      type="number"
                      value={formData.budget_min}
                      onChange={handleChange}
                      placeholder="500"
                      required
                      min="0"
                      data-testid="mission-budget-min-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="budget_max">Budget max (€) *</Label>
                    <Input
                      id="budget_max"
                      name="budget_max"
                      type="number"
                      value={formData.budget_max}
                      onChange={handleChange}
                      placeholder="800"
                      required
                      min="0"
                      data-testid="mission-budget-max-input"
                    />
                  </div>
                </div>
              </div>

              {/* Deadline */}
              <div className="space-y-4">
                <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-orange-500" />
                  Délai
                </h3>
                <div className="space-y-2">
                  <Label htmlFor="deadline">Date limite de livraison *</Label>
                  <Input
                    id="deadline"
                    name="deadline"
                    type="datetime-local"
                    value={formData.deadline}
                    onChange={handleChange}
                    required
                    data-testid="mission-deadline-input"
                  />
                </div>
              </div>

              {/* Submit */}
              <div className="flex gap-4 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate(-1)}
                  className="flex-1"
                >
                  Annuler
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-orange-500 hover:bg-orange-600"
                  disabled={loading}
                  data-testid="submit-mission-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Création...
                    </>
                  ) : (
                    'Créer la mission'
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default NewMissionPage;
